import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ophelos/ui/components/accordion';
import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Dialog, DialogContent, DialogTitle } from '@ophelos/ui/components/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@ophelos/ui/components/drawer';
import { ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { AffordabilityCommitment } from '@/components/payments/AffordabilityCommitment';
import { MultibancoReference } from '@/components/payments/MultibancoReference';
import { DiscountAlert } from '@/components/payments/DiscountAlert';
import { PayerEmailField } from '@/components/payments/PayerEmailField';
import { isSameDay, PaymentDatePicker } from '@/components/payments/PaymentDatePicker';
import { usePaymentOptions } from '@/components/payments/PaymentOptions';
import { PaymentMethodTab } from '@/pages/_partials/_singlePaymentPartials/PaymentMethodTab';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { useAuth } from '@/contexts/customer-auth-context';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useBalanceReference } from '@/hooks/useBalanceReference';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePayerEmail } from '@/hooks/usePayerEmail';
import { useTranslation } from '@/hooks/useTranslation';
import { ConnectApiError, mintBalanceReferenceOnConnect } from '@/lib/connect-api';
import { mintFailureAction, noMintCopyKey } from '@/lib/balance-reference';
import { discountedAmountMillicents } from '@/lib/discount-offers';
import { type MultibancoReference as MultibancoReferenceType, PaymentApiError, usePaymentApi } from '@/lib/payment-api';
import { buildScheduleForDate, toScheduleDate } from '@/lib/payment-schedule';
import { applyCredit, creditApplied } from '@/lib/accountNet';
import { formatMoneyInLanguage, toSubunits } from '@/lib/currency';
import { ACCOUNT_SCOPE, paymentReturnUrl } from '@/lib/paymentReturnUrl';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import type { DebtData, PaymentOptionsPlacement } from '@/types';

/** Card is always available; Multibanco only for the untouched full balance
 *  taken today, since the shared code is bound to the server-computed amount. */
const ACCOUNT_PAYMENT_METHODS: PaymentMethod[] = ['card', 'multibanco'];

interface AccountPayInFullTabProps {
  accountId: string | null;
  debts: DebtData[];
  amount: number;
  allowFullPayLater?: boolean; // If false, locks date to "today"
  onChoosePlan?: () => void;
  onChoosePartial?: () => void;
  planDiscountLabel?: string | null;
  optionsPlacement?: PaymentOptionsPlacement;
}

export function AccountPayInFullTab({
  accountId,
  debts,
  amount,
  allowFullPayLater = true,
  onChoosePlan,
  onChoosePartial,
  planDiscountLabel = null,
  optionsPlacement = 'inline',
}: AccountPayInFullTabProps) {
  const { t, currentLanguage } = useTranslation();
  const { createPaymentSession } = usePaymentApi();
  const { activeOffers, offersLoading } = useDebtData();
  const { authStage, getAccessToken } = useAuth();
  const balanceReference = useBalanceReference();
  const balanceReferenceEnabled = balanceReference.isEnabled;
  const isMobile = useIsMobile();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [multibancoRef, setMultibancoRef] = useState<MultibancoReferenceType | null>(null);
  const { needsEmail, payerEmail, setPayerEmail, emailValid, persistPayerEmail } = usePayerEmail();
  const options = usePaymentOptions({ onChoosePlan, onChoosePartial, planDiscountLabel, placement: optionsPlacement });

  // Combined discount across cases: each eligible case contributes its own pay_now
  // discount; cases without an offer pay full. Soonest expiry drives the countdown.
  // `perDebt` powers the optional breakdown so the customer can see which cases
  // got the offer when several are bundled together.
  const { displayAmount, payableAmount, earliestExpiry, hasActiveDiscount, perDebt } = useMemo(() => {
    const rows: Array<{ id: string; creditor: string; original: number; discounted: number; hasOffer: boolean }> = [];
    let total = 0;
    // Each debt's credit comes off that debt's own price, and only then are they summed:
    // pooling the credits first would let one debt's surplus pay down another, and the
    // total would stop matching the per-debt figures the customer was shown.
    let payable = 0;
    let credits = 0;
    let hasDiscount = false;
    let earliest: number | null = null;
    for (const debt of debts) {
      const off = activeOffers[debt.id];
      const debtCurrentMillicents = Math.round(debt.currentBalance * 100_000);
      const dMc = off ? discountedAmountMillicents(off, 'pay_now', debtCurrentMillicents) : null;
      const active = off && dMc != null && off.status !== 'expired' && off.status !== 'superseded';
      const discounted = active ? (dMc as number) / 100_000 : debt.currentBalance;
      rows.push({
        id: debt.id,
        creditor: debt.originalCreditor,
        original: debt.currentBalance,
        discounted,
        hasOffer: Boolean(active),
      });
      total += discounted;
      payable += applyCredit(discounted, debt.creditApplied ?? 0);
      credits += debt.creditApplied ?? 0;
      if (active) {
        hasDiscount = true;
        if (off.expiresAt) {
          const t = new Date(off.expiresAt).getTime();
          if (earliest === null || t < earliest) earliest = t;
        }
      }
    }
    return {
      displayAmount: hasDiscount ? total : amount,
      // Only the discounted path needs the per-debt walk. Without a discount each debt's
      // credit is capped at its own balance already, so the aggregate cannot overflow onto
      // another debt, and the prop stays the caller's figure to own.
      payableAmount: hasDiscount ? payable : applyCredit(amount, credits),
      earliestExpiry: earliest != null ? new Date(earliest).toISOString() : null,
      hasActiveDiscount: hasDiscount,
      perDebt: rows,
    };
  }, [debts, activeOffers, amount]);

  const canShowBreakdown = debts.length > 1;

  const showDiscount = hasActiveDiscount;
  // Credit applies after the discount, and here rather than to the `amount` prop because
  // the discount path above rebuilds the total from the per-case balances and drops it.
  const effectiveAmount = payableAmount;
  // Guarded on what is actually charged, not the gross: a discount can drop the balance
  // under the credit while the gross still exceeds it, which would charge zero.
  const nothingLeftToPay = creditApplied(debts) > 0 && effectiveAmount <= 0;
  const totalSavings = showDiscount ? amount - displayAmount : 0;

  const today = new Date();
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + 14);

  const isToday = selectedDate && isSameDay(selectedDate, today);

  const currency = debts[0]?.currency ?? 'EUR';

  // An absent offer only rules a discount out once the lookup has resolved. A code
  // bound to the undiscounted balance cannot be withdrawn afterwards, so treat an
  // in-flight lookup as "a discount may apply" — same rule as the case grain.
  const discountMayApply = offersLoading || showDiscount;
  const canUseMultibanco = balanceReferenceEnabled && !discountMayApply && Boolean(isToday);
  // A method the customer can no longer use must not stay selected — losing
  // eligibility mid-flow (they pick a future date) would otherwise leave
  // Multibanco armed with the card path hidden behind it.
  const isPayingByMultibanco = canUseMultibanco && paymentMethod === 'multibanco';

  const handleContinue = async () => {
    if (!accountId || isProcessing || nothingLeftToPay) return;
    if (!emailValid) {
      setPaymentError(t('paymentPlan.emailRequired'));
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    // Multibanco pay-in-full serves the account's shared code and creates no
    // checkout session, so there is no row to strand and no amount to send —
    // the server computes what the code covers.
    if (isPayingByMultibanco) {
      try {
        await persistPayerEmail();
        const minted = await mintBalanceReferenceOnConnect(authStage, getAccessToken);
        setIsProcessing(false);
        if (minted.reference) {
          setMultibancoRef(minted.reference);
          // A first-ever mint leaves the card's own read empty; refresh it so the
          // code stays on screen after the dialog closes.
          balanceReference.refetch();
          return;
        }
        // No reference: say which empty state it was. 'in_arrangement' and
        // 'balance_changed' both still owe money, so neither may claim there is
        // nothing to pay.
        setPaymentError(t(noMintCopyKey(minted.reason)));
        return;
      } catch (err) {
        setIsProcessing(false);
        // Contention and rate limiting both clear on their own, so tell the
        // customer to retry instead of reporting a failure they can't act on.
        const retryable = err instanceof ConnectApiError && mintFailureAction(err.status) === 'tell-customer-to-retry';
        setPaymentError(t(retryable ? 'payment.referenceInProgress' : 'payment.processingFailed'));
        return;
      }
    }

    try {
      // A future date turns the payment into a single-installment plan; a live
      // pay-now discount never applies to a scheduled charge, so the saved
      // banner is only threaded for an immediate (today) full pay. Scheduling is
      // card-only for now — SEPA scheduling is deferred.
      const isScheduled = !!selectedDate && !isToday;
      const baseUrl = window.location.origin;
      const savedAmount = !isScheduled && totalSavings > 0 ? totalSavings : 0;
      const successUrl = isScheduled
        ? paymentReturnUrl({ flow: 'scheduled', scope: ACCOUNT_SCOPE, date: toScheduleDate(selectedDate) })
        : paymentReturnUrl({ flow: 'immediate', scope: ACCOUNT_SCOPE, saved: savedAmount });
      const cancelUrl = `${baseUrl}/account-payment`;

      const whenLabel = isScheduled ? ` on ${selectedDate.toLocaleDateString()}` : '';

      // Persist a customer-supplied email (when none usable on file) before checkout.
      await persistPayerEmail();

      const response = await createPaymentSession({
        account_id: accountId,
        amount_subunits: isScheduled ? undefined : toSubunits(effectiveAmount),
        currency,
        payment_method: 'card',
        payment_type: 'full',
        description: `Full payment of ${effectiveAmount.toFixed(2)} ${currency} across all debts${whenLabel}`,
        success_url: successUrl,
        cancel_url: cancelUrl,
        payment_plan_schedule: isScheduled ? buildScheduleForDate(effectiveAmount, selectedDate) : undefined,
      });

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }

      throw new Error('Invalid payment response: no checkout URL returned');
    } catch (err) {
      setIsProcessing(false);
      if (err instanceof PaymentApiError) {
        // 409 here is a plan conflict, and the server's detail is written for plan
        // *creation* ("cancel it before creating another plan") — nonsense to a
        // customer trying to pay. Distinct from the Multibanco branch's
        // ConnectApiError 409, which is transient contention and does say retry.
        setPaymentError(err.status === 409 ? t('payment.planConflict') : err.message);
      } else {
        setPaymentError(t('payment.processingFailed'));
      }
    }
  };

  return (
    <div className="w-full flex-1 min-h-0 stack items-center px-3 xs:px-4 max-w-lg mx-auto">
      {showDiscount && (
        <DiscountAlert
          title={t('discount.appliedAcrossDebts')}
          saving={
            <Trans
              i18nKey="discount.youSaveAcrossDebts"
              components={{ amount: <CurrencyDisplay amount={totalSavings} currency={currency} className="font-semibold inline-flex gap-0" /> }}
            />
          }
          expiresAt={earliestExpiry}
        />
      )}
      <div className="flex-1 stack max-w-sm w-full">
        <div className="flex flex-col gap-8">
          {/* Amount Display */}
          <div className="stack items-center">
            <div className={`flex flex-col items-center gap-2 ${showDiscount ? 'mt-10' : 'mt-18'}`}>
              {showDiscount && <CurrencyDisplay amount={amount} currency={currency} className="text-muted-foreground line-through" />}
              <div className="text-5xl font-bold">
                <CurrencyDisplay amount={effectiveAmount} currency={currency} className="text-5xl" centsClassName="text-2xl" />
              </div>
            </div>

            {canShowBreakdown ? (
              <Accordion type="single" collapsible className="mt-2">
                <AccordionItem value="breakdown" className="border-b-0">
                  <AccordionTrigger className="py-1 justify-center gap-2 text-sm text-muted-foreground font-normal hover:bg-transparent">
                    {t('paymentTabs.combinedTotal')} •{' '}
                    <span className="text-foreground font-semibold">
                      {debts.length} {debts.length === 1 ? t('dashboard.debt') : t('dashboard.debts')}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="divide-y divide-border p-2">
                    {perDebt.map((row) => (
                      <div key={row.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <span className="truncate text-foreground">{row.creditor}</span>
                        <span className="flex items-center gap-2 text-right shrink-0">
                          {row.hasOffer ? (
                            <>
                              <CurrencyDisplay amount={row.original} currency={currency} className="line-through text-muted-foreground text-xs" />
                              <CurrencyDisplay amount={row.discounted} currency={currency} className="font-semibold" />
                            </>
                          ) : (
                            <CurrencyDisplay amount={row.original} currency={currency} className="font-semibold" />
                          )}
                        </span>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : (
              <span className="text-sm text-center text-muted-foreground block mt-2">
                {t('paymentTabs.combinedTotal')} •{' '}
                <span className="text-foreground font-semibold">
                  {debts.length} {debts.length === 1 ? t('dashboard.debt') : t('dashboard.debts')}
                </span>
              </span>
            )}
          </div>

          <div className="flex justify-center max-w-xs mx-auto w-full">
            <PaymentDatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              maxDate={maxDate}
              canDefer={allowFullPayLater}
              lockedCopyKey="paymentTabs.paymentMustBeToday"
              label={
                isToday
                  ? t('paymentTabs.payToday')
                  : selectedDate
                    ? `${t('paymentTabs.payOn')} ${selectedDate.toLocaleDateString()}`
                    : t('paymentTabs.selectDate')
              }
              data-testid="payment-action-date"
            />
          </div>

          {options.rows}

          {needsEmail && <PayerEmailField id="account-full-payer-email" value={payerEmail} onChange={setPayerEmail} />}

          {/* Multibanco is offered only for the full balance taken today: the
              shared code is bound to the balance the server computes, so a
              scheduled date or a discount would both price the payment away from
              it. Card stays the only option in those cases. */}
          <div className="w-full mt-8">
            <PaymentMethodTab
              paymentMethod={paymentMethod}
              onSelectMethod={setPaymentMethod}
              disabled={isProcessing}
              error={null}
              availableMethods={canUseMultibanco ? ACCOUNT_PAYMENT_METHODS : ['card']}
              canServeSharedReference={canUseMultibanco}
              balanceReference={balanceReference.reference}
              excludesArrangedDebts={balanceReference.excludesArrangedDebts}
            />
          </div>

          {paymentError && (
            <Alert variant="destructive">
              <AlertDescription>{paymentError}</AlertDescription>
            </Alert>
          )}
        </div>

        <AffordabilityCommitment />
      </div>

      {options.menu}

      <BottomActionBar>
        <div className="flex w-full items-center justify-center gap-4">
          {options.button}
          <Button onClick={handleContinue} disabled={isProcessing || !emailValid || nothingLeftToPay} size="xl" className="group">
            {/* Multibanco issues a code to pay later at an ATM — nothing is charged
                on this click, so the card's "Pay €X now" would misdescribe it. */}
            {isPayingByMultibanco
              ? t('paymentMethod.getMultibancoReference')
              : selectedDate && !isToday
                ? t('paymentTabs.schedulePayment')
                : t('paymentTabs.payNow', { amount: formatMoneyInLanguage(effectiveAmount, currentLanguage, currency) })}
            <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </BottomActionBar>

      {isMobile ? (
        <Drawer open={!!multibancoRef} onOpenChange={(open) => !open && setMultibancoRef(null)}>
          <DrawerContent className="h-full bg-card">
            <DrawerTitle className="sr-only">{t('multibanco.title')}</DrawerTitle>
            {multibancoRef && <MultibancoReference reference={multibancoRef} onClose={() => setMultibancoRef(null)} />}
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={!!multibancoRef} onOpenChange={(open) => !open && setMultibancoRef(null)}>
          <DialogContent className="max-w-2xl! h-full max-h-[95vh]! overflow-hidden no-scrollbar rounded-2xl">
            <DialogTitle className="sr-only">{t('multibanco.title')}</DialogTitle>
            {multibancoRef && <MultibancoReference reference={multibancoRef} onClose={() => setMultibancoRef(null)} />}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
